import React, { useState, useMemo, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Helmet } from "react-helmet";
import { materialPropertyService } from "/app/src/services";
import { useTranslation } from "react-i18next";
import { MaterialProperty as MaterialPropertyType } from "/app/src/models";
import { createColumnHelper } from "@tanstack/react-table";
import Table from "/app/src/components/generic/tables/table";
import { useSortUpgrade } from "/app/src/hooks/useSortUpgrade";
import usePaginatedData from "/app/src/hooks/usePaginatedData";
import useQueryParams from "/app/src/hooks/useQueryParams";
import {
  Button,
  Checkbox,
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@heroui/react";

import useSetting from "/app/src/components/settings/setting";
import {
  buildExplorerColumns,
  isColumnSelected,
  toggleExplorerColumn,
} from "/app/src/components/explorer/helpers/columns";
import HeroSearch from "/app/src/components/generic/components/heroSearch";
import Breadcrumbs from "/app/src/components/generic/components/breadcrumbs";
import { useAuthState } from "/app/src/contexts/authentication";
/**
 * Renders the material properties explorer component which displays a table of material properties.
 *
 * @returns {JSX.Element} The rendered material properties component.
 */
export default function MaterialProperties() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [sort, setSort] = useSortUpgrade([]);
  const [searchString, setSearchString] = useState(
    searchParams.get("search") || "",
  );
  const [page, setPage] = useState(
    parseInt(searchParams.get("page") || "1") - 1,
  );
  const [pageSize, setPageSize] = useState(
    parseInt(searchParams.get("pageSize") || "25"),
  );

  const { user } = useAuthState();
  const columnHelper = createColumnHelper<MaterialPropertyType>();

  const availableColumns = [
    { id: "description", label: t("translation:description") },
    { id: "countingCycle", label: t("translation:cycle_counting") },
    { id: "expiryDays", label: t("translation:expiry_days") },
  ];

  const { settingValue, createUpdateSetting } = useSetting({
    name: "explorerColumns",
    userId: user?.id,
  });

  const isColumnChecked = useCallback(
    (id: string) => isColumnSelected(settingValue, "material_property", id),
    [settingValue],
  );

  const toggleColumnSelection = useCallback(
    async (id: string) =>
      toggleExplorerColumn(
        settingValue,
        createUpdateSetting,
        "material_property",
        id,
        user?.id,
      ),
    [settingValue, createUpdateSetting, user?.id],
  );

  const columns = useMemo(() => {
    /**
     * Creates a column definition based on the given id.
     * @param {string} id - The identifier of the column to create.
     * @returns {Object|null} The column definition object or null if the id is not recognized.
     */
    const makeColumn = (id: string) => {
      switch (id) {
        case "name":
          return columnHelper.accessor("name", {
            id: "name",
            cell: (info) => info.getValue(),
            header: () => t("translation:name"),
          });
        case "description":
          return columnHelper.accessor("description", {
            id: "description",
            cell: (info) => info.getValue(),
            header: () => t("translation:description"),
          });
        // case "serial":
        //   return columnHelper.accessor("isSerialNumberMixed", {
        //     id: "isSerialNumberMixed",
        //     cell: (info) => info.getValue(),
        //     header: () => t("translation:serial_number"),
        //   });
        // case "lot":
        //   return columnHelper.accessor("isLotMixed", {
        //     id: "isLotMixed",
        //     cell: (info) => info.getValue(),
        //     header: () => t("translation:lot_number"),
        //   });
        case "countingCycle":
          return columnHelper.accessor("countingCycle", {
            id: "countingCycle",
            cell: (info) => info.getValue(),
            header: () => t("translation:cycle_counting"),
          });
        case "expiryDays":
          return columnHelper.accessor("expiryDays", {
            id: "expiryDays",
            cell: (info) => info.getValue(),
            header: () => t("translation:expiry_days"),
          });
        default:
          return null;
      }
    };
    return buildExplorerColumns(
      settingValue,
      "material_property",
      makeColumn,
      "name",
    );
  }, [columnHelper, settingValue, t]);

  const goToMaterialProperty = useCallback(
    (materialPropertyId: number) => {
      navigate(`/explorer/material_properties/${materialPropertyId}`);
    },
    [navigate],
  );

  const createToggleHandler = useCallback(
    (id: string) => () => {
      toggleColumnSelection(id);
    },
    [toggleColumnSelection],
  );

  const {
    data: materialProperties,
    count: materialPropertiesCount,
    isFetching,
  } = usePaginatedData<MaterialPropertyType>({
    queryKey: ["material_properties"],
    searchString,
    sort,
    page,
    pageSize,
    service: materialPropertyService,
  });

  const { updatePage, updatePageSize } = useQueryParams({
    count: materialPropertiesCount,
    isFetching,
    page,
    setPage,
    pageSize,
    setPageSize,
  });

  return (
    <div className="explorer">
      <Helmet>
        <title>{t("translation:material_properties")} - ItemPath</title>
      </Helmet>
      <Breadcrumbs
        items={[
          { label: "Explorer", to: "/explorer" },
          { label: t("translation:material_properties") },
        ]}
      />

      <div className="mt-12 mb-4 flex justify-between">
        <div className="flex justify-between gap-2">
          <HeroSearch
            className="w-[300px]"
            setSearchString={setSearchString}
            allowClear
            box={false}
          />
        </div>
        <Popover placement="bottom" radius="md" aria-label="Customize columns">
          <PopoverTrigger>
            <Button size="md" radius="md" color="primary">
              {t("translation:customize_columns")}
            </Button>
          </PopoverTrigger>
          <PopoverContent>
            <div className="w-[300px] p-4">
              <h3 className="text-[16px]">
                {t("translation:available_columns")}
              </h3>
              <div className="mt-2 flex flex-col gap-2">
                {availableColumns.map((column) => (
                  <Checkbox
                    size="sm"
                    key={column.id}
                    defaultSelected={isColumnChecked(column.id)}
                    onChange={createToggleHandler(column.id)}
                  >
                    {column.label}
                  </Checkbox>
                ))}
              </div>
            </div>
          </PopoverContent>
        </Popover>
      </div>
      <Table
        loading={isFetching}
        rows={materialProperties}
        tableColumns={columns}
        length={materialPropertiesCount}
        sort={sort}
        setSort={setSort}
        paginationEnabled={{
          currentPage: page,
          pageSize,
          setPage: updatePage,
          setPageSize: updatePageSize,
        }}
        rowClicked={goToMaterialProperty}
        emptyText={`${t("translation:no")} ${t(
          "translation:material_properties",
        )} ${t("translation:found")}`}
      />
    </div>
  );
}
